import * as React from 'react';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { CircleIcon } from 'lucide-react';

import { Label } from './label';
import { cn } from '../../lib/utils';

const radioGroupVariants = cva('grid gap-1.5', {
  variants: {
    variant: {
      default: 'outline-solid',
      outline: 'rounded-md p-4 !outline-1 outline outline-offset-[-1px] !outline-stroke',
    },
  },
  defaultVariants: { variant: 'default' },
});

interface RadioGroupProps
  extends
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    VariantProps<typeof radioGroupVariants> {
  alignment?: 'left' | 'right';
}

const RadioGroup = ({
  className,
  variant,
  disabled,
  ref,
  ...props
}: RadioGroupProps & { ref?: React.Ref<React.ElementRef<typeof RadioGroupPrimitive.Root>> }) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn(radioGroupVariants({ variant }), className, { 'opacity-50': disabled })}
      disabled={disabled}
      {...props}
      ref={ref}
    />
  );
};

interface RadioGroupItemProps extends React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
> {
  alignment?: 'left' | 'right';
  description?: string;
}

const RadioGroupItem = ({
  className,
  children,
  alignment = 'left',
  description,
  ref,
  ...props
}: RadioGroupItemProps & {
  ref?: React.Ref<React.ElementRef<typeof RadioGroupPrimitive.Item>>;
}) => {
  return (
    <div className="space-y-1">
      <div
        className={cn('flex w-full items-center gap-2', {
          'flex-row-reverse justify-between': alignment === 'right',
        })}
      >
        <RadioGroupPrimitive.Item
          ref={ref}
          className={cn(
            'aspect-square h-4 w-4 rounded-full border border-foreground-muted text-accent ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          {...props}
        >
          <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
            <CircleIcon className="h-2.5 w-2.5 fill-current text-current" />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
        {children && (
          <Label
            htmlFor={props.id}
            className={cn(
              'text-sm leading-none font-normal text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
              description && 'font-medium'
            )}
          >
            {children}
          </Label>
        )}
      </div>
      {description && (
        <div
          className={cn(
            'text-sm leading-5 text-foreground-muted',
            alignment === 'right' ? 'pr-6' : 'pl-6'
          )}
        >
          {description}
        </div>
      )}
    </div>
  );
};

export { RadioGroup, RadioGroupItem };
